import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { resolveTriggerListHistoryId } from 'utils/resolveSiteWorkflowIds'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Accordion from 'components/Accordion'
import Modal from 'components/Modal'
import RemoveCriterionEModal from './removeCriterionE'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID } from 'constants/siteStatuses'
import { deleteCriterionE, requestGetCriterionEs, requestUpdateCriterionE } from 'api/criterionE'
import { AssessmentPageSubTittle, AssessmentPageSubTittleIconWrapper, AssessmentPageSubTittleWrapper, AssessmentPageTittle, AssessmentPageTittleWrapper } from '../../style'
import AccordionContent from './accordionContent'
import AccordionHeader from './accordionHeader'
import { EcosystemListTitle, EcosystemListTitleWrapper } from '../ecosystemData/style'
import { getCriterionEId, normalizeCriterionEItems } from 'utils/criterionEHelpers'
import { useOverallKbaCriteriaRefresh } from 'flow/siteProposal/pages/assessment/context/OverallKbaCriteriaRefreshContext'

const defaultCriterionE = {
    year: '',
    irreplaceability_score: '',
    irreplaceability_assessment_approved: false,
    trigger_reason_id: [],
    trigger_reason_text: '',
    reassessment_flag: false,
}

const tooltipXOffset = 0
const tooltipYOffset = 16
const criterionEItemPropType = PropTypes.shape({
  site_irreplaceability_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_irreplaceability_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  is_approved_by_secretariat: PropTypes.bool,
  last_reassessment_date: PropTypes.string,
  created_at: PropTypes.string,
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
})

const CriterionEList = ({ site }) => {
    const params = useParams()
    const { refetchCriteria } = useOverallKbaCriteriaRefresh()
    const [criterionEs, setCriterionEs] = useState([])
    const [isRemoveCriterionEModalOpen, setIsRemoveCriterionEModalOpen] = useState(false)
    const [willBeDeletedCriterionE, setWillBeDeletedCriterionE] = useState({})

    async function getCriterionEs() {
        const data = await requestGetCriterionEs(site.id, resolveTriggerListHistoryId(site, params))
        setCriterionEs(normalizeCriterionEItems(data))
    }

    async function requestDeleteCriterionE(criterionEId) {
        await deleteCriterionE(criterionEId, params.siteHistoryId)
        await getCriterionEs()
        refetchCriteria()
    }

    const openRemoveCriterionEModal = async (criterion) => {
        setIsRemoveCriterionEModalOpen(true)
        setWillBeDeletedCriterionE(criterion)
    }

    const updateCriterionE = async (data) => {
        try {
            const criterionEId = data.criterionEId
            if (!criterionEId) {
                console.error('updateCriterionE: criterionEId is required')
                return
            }
            const updatePayload = { ...data }
            delete updatePayload.criterionEId
            const dataCrit = criterionEs.find((item) => getCriterionEId(item) === criterionEId)
            let criterionE

            if (dataCrit?.trigger_reason_id === undefined || (Array.isArray(dataCrit.trigger_reason_id) && dataCrit.trigger_reason_id.length === 0)) {
                criterionE = await requestUpdateCriterionE(criterionEId, params.siteHistoryId, { ...updatePayload, trigger_reason_id: [1] })
            } else if (Array.isArray(dataCrit.trigger_reason_id) && dataCrit.trigger_reason_id.length > 0) {
                criterionE = await requestUpdateCriterionE(criterionEId, params.siteHistoryId, updatePayload)
            } else {
                criterionE = await requestUpdateCriterionE(criterionEId, params.siteHistoryId, { ...updatePayload, trigger_reason_id: [1] })
            }

            if (!criterionE) {
                console.error('updateCriterionE: No criterionE returned from API')
                return
            }

            if (criterionEs.length === 0) {
                return setCriterionEs([criterionE])
            }
            
            setCriterionEs((s) => {
                const tmpCopy = [...s]
                const updatedCriterionEId = getCriterionEId(criterionE)
                const updatedIndex = tmpCopy.findIndex((system) => getCriterionEId(system) === updatedCriterionEId)
                if (updatedIndex !== -1) {
                    tmpCopy[updatedIndex] = { ...criterionE }
                }
                return tmpCopy
            })
            getCriterionEs()
            refetchCriteria()
        } catch (error) {
            console.error('Error updating criterion E:', error)
        }
    }

    useEffect(() => {
        getCriterionEs()
    }, [])

    if (criterionEs.length === 0 && site.SiteStatusId !== SITE_STATUS_DRAFT_ID) {
        return null
    }

    const approvedCriterionEs = criterionEs.filter((item) => item?.is_approved_by_secretariat)
    const rejectedCriterionEs = criterionEs.filter((item) => !item?.is_approved_by_secretariat)

    const newCriterionEs = criterionEs.filter((item) => !item?.last_reassessment_date)
    const oldCriterionEs = criterionEs.filter((item) => item?.last_reassessment_date)

    return (
        <div>
            <Container>
                <Row>
                    <Col md={12}>
                        <AssessmentPageTittleWrapper>
                            <AssessmentPageTittle data-testid="legend-irreplaceability">
                                <FormattedMessage id="irreplaceability" />
                            </AssessmentPageTittle>
                            <AssessmentPageSubTittleWrapper marginBottom="64px" data-testid="sub-legend-irreplaceability">
                                <AssessmentPageSubTittle>
                                    <FormattedMessage id="irreplaceabilitySubTittle" />
                                </AssessmentPageSubTittle>
                                <Tooltip
                                    style={{ zIndex: 1000 }}
                                    placement="bottom"
                                    trigger={['hover']}
                                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                                    overlay={{ label: 'irreplaceability', infoTooltip: 'irreplaceabilityHint' }}
                                    type="big"
                                >
                                    <AssessmentPageSubTittleIconWrapper>
                                        <InfoIcon />
                                    </AssessmentPageSubTittleIconWrapper>
                                </Tooltip>
                            </AssessmentPageSubTittleWrapper>
                        </AssessmentPageTittleWrapper>
                    </Col>
                </Row>
                <Row>
                    <Col md={8} mdOffset={2} dataTest='criterionE-group'>
                        {criterionEs.length === 0 ? (
                            <AccordionContent item={defaultCriterionE} updateCriterionE={updateCriterionE} siteId={site.id} defaultPage={true} site={site} criterionEs={criterionEs} />
                        ) : (
                            <>
                                {site.SiteStatusId === SITE_STATUS_NOMINATED_ID ? (
                                    <>
                                        {approvedCriterionEs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper>
                                                            <EcosystemListTitle marginTop='0'>
                                                                <FormattedMessage
                                                                    id='approvedTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={approvedCriterionEs}
                                                    contentProps={{ updateCriterionE, site, criterionEs }}
                                                    headerProps={{ openRemoveCriterionEModal, getCriterionEs, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                    initialActive={null}
                                                />
                                            </>
                                        )}
                                        {rejectedCriterionEs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper>
                                                            <EcosystemListTitle marginTop='48px'>
                                                                <FormattedMessage
                                                                    id='rejectedTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={rejectedCriterionEs}
                                                    contentProps={{ updateCriterionE, site, criterionEs }}
                                                    headerProps={{ openRemoveCriterionEModal, getCriterionEs, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                    initialActive={null}
                                                />
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {oldCriterionEs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper>
                                                            <EcosystemListTitle>
                                                                <FormattedMessage id='existingTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={oldCriterionEs}
                                                    contentProps={{ updateCriterionE, site, criterionEs }}
                                                    headerProps={{ openRemoveCriterionEModal, getCriterionEs, oldTriggers: true, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                    initialActive={null}
                                                />
                                            </>
                                        )}
                                        {newCriterionEs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper addMarginTop={true}>
                                                            <EcosystemListTitle>
                                                                <FormattedMessage id='newTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={newCriterionEs}
                                                    contentProps={{ updateCriterionE, site, criterionEs }}
                                                    headerProps={{ openRemoveCriterionEModal, getCriterionEs, newTriggers: true, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Col>
                </Row>
            </Container>
            <Modal
                width={'416px'}
                isOpen={isRemoveCriterionEModalOpen}
                toggleModal={setIsRemoveCriterionEModalOpen}
                modalTitleId='removeCriterionE'
                bottomBorder={true}
            >
                <RemoveCriterionEModal
                    item={willBeDeletedCriterionE}
                    closeModal={() => {
                        setIsRemoveCriterionEModalOpen(false)
                    }}
                    confirmRemove={() => {
                        requestDeleteCriterionE(getCriterionEId(willBeDeletedCriterionE))
                        setIsRemoveCriterionEModalOpen(false)
                    }}
                />
            </Modal>
        </div>
    )
}

export default CriterionEList

CriterionEList.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    SiteStatusId: PropTypes.number.isRequired,
    sitehistoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reassessmentFlag: PropTypes.bool,
    criterionEs: PropTypes.arrayOf(criterionEItemPropType)
  }).isRequired
}